import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from '@chakra-ui/react';
import { emptyUserEditorFields, useUserEditorProvider } from '../../../providers/UserEditor/userEditorProvider';
import { useBlockchainProvider } from '../../../providers/Blockchain/blockchainProvider';
import { t } from 'i18next';
import { useUserProvider } from '../../../providers/User/userProvider';
import { ethers } from 'ethers';
import { useReflink } from '../../../providers/Reflink/useReflink';
import { createStandaloneToast } from '@chakra-ui/react';
import { contrastColors } from '../../../utils/parser';

enum UserDataState {
	UNCHANGED,
	CHANGED,
	PROCESSING,
}

export const Editor = () => {
	const editorData = useUserEditorProvider();
	const { data } = useUserProvider();

	const { contracts } = useBlockchainProvider();

	const { getReflink } = useReflink();
	const currentReferrer = getReflink ?? ethers.constants.AddressZero;

	const [dataState, handleDataState] = useState(UserDataState.UNCHANGED);

	const { toast } = createStandaloneToast();

	const handleSaveChanges = async () => {
		if (!contracts.userProfile) {
			return;
		}

		handleDataState(UserDataState.PROCESSING);

		try {
			const transaction = await contracts.userProfile.updateProfile(
				editorData.nickname ?? data.nickname,
				editorData.color ?? data.color,
				editorData.avatar ?? data.avatar,
				editorData.description ?? data.description,
				JSON.stringify(editorData.socials) ?? data.socials,
				currentReferrer
			);
			await transaction.wait();
		} catch (error) {
			handleDataState(UserDataState.CHANGED);

			toast({ status: 'error', title: t('user.uploadErr').toString() });
		}

		editorData.updateValues(emptyUserEditorFields);
		handleDataState(UserDataState.UNCHANGED);

		toast({ status: 'success', title: t('user.uploadSuccess').toString() });
	};

	const buttonColor = useMemo(
		() => (contrastColors(editorData.color ?? data.color, '#ffffff') > 3.1 ? 'white' : 'black'),
		[data.color, editorData.color]
	);

	const typeOptions: { [p: number]: () => JSX.Element } = {
		[UserDataState.CHANGED]: () => (
			<Button
				variant="unstyled"
				bg={editorData.color ?? data.color}
				color={buttonColor}
				padding="0 16px"
				_hover={{}}
				onClick={handleSaveChanges}
			>
				{t('editor.save').toString()}
			</Button>
		),
		[UserDataState.UNCHANGED]: () => <></>,
		[UserDataState.PROCESSING]: () => <Spinner size="md" />,
	};

	const connectType = useMemo(() => {
		if (dataState === UserDataState.CHANGED) {
			return UserDataState.CHANGED;
		}
		if (dataState === UserDataState.UNCHANGED) {
			return UserDataState.UNCHANGED;
		}
		if (dataState === UserDataState.PROCESSING) {
			return UserDataState.PROCESSING;
		}
		return UserDataState.UNCHANGED;
	}, [dataState]);

	useEffect(() => {
		if (
			editorData.nickname !== null ||
			editorData.avatar !== null ||
			editorData.color !== null ||
			editorData.description !== null ||
			editorData.socials !== null
		) {
			handleDataState(UserDataState.CHANGED);
		} else {
			handleDataState(UserDataState.UNCHANGED);
		}
	}, [data, editorData]);

	return typeOptions[connectType]();
};
